import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, PageHeader, Textarea } from '@/components/ui'
import { cn } from '@/lib/cn'
import {
  Truck, Package, MapPin, Calendar, Search, Plus, Settings2, Eye, Copy, CheckCircle2, AlertCircle, Clock, Zap, ExternalLink,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/shipping')({
  component: StoreShippingPage,
})

type ShippingProvider = 'interrapidisimo' | 'servientrega' | 'coordinadora' | 'dhl'
type GuideStatus = 'draft' | 'generated' | 'in_transit' | 'delivered' | 'cancelled'
type EventType = 'created' | 'manifested' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'failed' | 'returned'

interface ShippingGuide {
  id: string
  guideNumber: string
  orderId: string
  orderNumber: string
  provider: ShippingProvider
  recipient: {
    name: string
    phone: string
    email: string
    address: string
    city: string
    zip: string
  }
  weight: number // kg
  value: number
  status: GuideStatus
  trackingUrl: string
  createdAt: string
  deliveredAt?: string
  events: ShippingEvent[]
}

interface ShippingEvent {
  id: string
  type: EventType
  timestamp: string
  location: string
  description: string
}

interface ProviderConfig {
  id: ShippingProvider
  name: string
  logo: string
  color: string
  isConfigured: boolean
  apiKey?: string
  apiUrl: string
}

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'interrapidisimo',
    name: 'Interrapidísimo',
    logo: '📦',
    color: 'text-yellow-500',
    isConfigured: true,
    apiUrl: 'https://api.interrapidisimo.com/v1',
  },
  {
    id: 'servientrega',
    name: 'Servientrega',
    logo: '🚚',
    color: 'text-blue-500',
    isConfigured: false,
    apiUrl: 'https://api.servientrega.com.co',
  },
  {
    id: 'coordinadora',
    name: 'Coordinadora',
    logo: '🏢',
    color: 'text-red-500',
    isConfigured: false,
    apiUrl: 'https://api.coordinadora.com',
  },
  {
    id: 'dhl',
    name: 'DHL',
    logo: '✈️',
    color: 'text-[hsl(var(--primary))]',
    isConfigured: false,
    apiUrl: 'https://api.dhl.com',
  },
]

const SEED_GUIDES: ShippingGuide[] = [
  {
    id: 'guide-001',
    guideNumber: 'INTER-2026-05-001',
    orderId: 'ord-123',
    orderNumber: '#ORD-2026-001',
    provider: 'interrapidisimo',
    recipient: {
      name: 'Juan López',
      phone: '+57 301 234 5678',
      email: 'juan@example.com',
      address: 'Cra 7 #45-89, Apt 302',
      city: 'Bogotá',
      zip: '110221',
    },
    weight: 2.5,
    value: 85000,
    status: 'delivered',
    trackingUrl: 'https://tracking.interrapidisimo.com/INTER-2026-05-001',
    createdAt: '2026-05-01T10:30:00Z',
    deliveredAt: '2026-05-06T14:20:00Z',
    events: [
      { id: 'ev-1', type: 'created', timestamp: '2026-05-01T10:30:00Z', location: 'Bogotá', description: 'Guía creada' },
      { id: 'ev-2', type: 'manifested', timestamp: '2026-05-01T15:45:00Z', location: 'Centro de Distribución Bogotá', description: 'Paquete recibido' },
      { id: 'ev-3', type: 'in_transit', timestamp: '2026-05-03T08:00:00Z', location: 'En ruta a destino', description: 'En tránsito' },
      { id: 'ev-4', type: 'out_for_delivery', timestamp: '2026-05-06T09:15:00Z', location: 'Cali', description: 'Paquete en reparto' },
      { id: 'ev-5', type: 'delivered', timestamp: '2026-05-06T14:20:00Z', location: 'Cra 7 #45-89', description: 'Entregado exitosamente' },
    ],
  },
  {
    id: 'guide-002',
    guideNumber: 'INTER-2026-05-002',
    orderId: 'ord-124',
    orderNumber: '#ORD-2026-002',
    provider: 'interrapidisimo',
    recipient: {
      name: 'María García',
      phone: '+57 310 987 6543',
      email: 'maria@example.com',
      address: 'Av Paseo de la República 500',
      city: 'Medellín',
      zip: '050001',
    },
    weight: 1.2,
    value: 65000,
    status: 'in_transit',
    trackingUrl: 'https://tracking.interrapidisimo.com/INTER-2026-05-002',
    createdAt: '2026-05-05T14:00:00Z',
    events: [
      { id: 'ev-1', type: 'created', timestamp: '2026-05-05T14:00:00Z', location: 'Bogotá', description: 'Guía creada' },
      { id: 'ev-2', type: 'manifested', timestamp: '2026-05-05T16:30:00Z', location: 'Centro de Distribución Bogotá', description: 'Paquete recibido' },
      { id: 'ev-3', type: 'in_transit', timestamp: '2026-05-06T09:00:00Z', location: 'En ruta a Medellín', description: 'En tránsito' },
    ],
  },
  {
    id: 'guide-003',
    guideNumber: 'INTER-2026-05-003',
    orderId: 'ord-125',
    orderNumber: '#ORD-2026-003',
    provider: 'interrapidisimo',
    recipient: {
      name: 'Carlos Rodríguez',
      phone: '+57 315 456 7890',
      email: 'carlos@example.com',
      address: 'Calle 10 #20-45',
      city: 'Barranquilla',
      zip: '080001',
    },
    weight: 0.8,
    value: 45000,
    status: 'generated',
    trackingUrl: 'https://tracking.interrapidisimo.com/INTER-2026-05-003',
    createdAt: '2026-05-07T11:00:00Z',
    events: [
      { id: 'ev-1', type: 'created', timestamp: '2026-05-07T11:00:00Z', location: 'Bogotá', description: 'Guía creada' },
    ],
  },
]

function StoreShippingPage() {
  const [guides, setGuides] = useState(SEED_GUIDES)
  const [selectedGuide, setSelectedGuide] = useState<string | null>(guides[0]?.id ?? null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | GuideStatus>('all')
  const [showNewGuideForm, setShowNewGuideForm] = useState(false)
  const [newGuideData, setNewGuideData] = useState({
    recipient: '',
    address: '',
    city: '',
    weight: '1',
    value: '50000',
    provider: 'interrapidisimo' as ShippingProvider,
  })

  const selected = guides.find((g) => g.id === selectedGuide)

  const stats = useMemo(() => ({
    total: guides.length,
    generated: guides.filter((g) => g.status === 'generated').length,
    inTransit: guides.filter((g) => g.status === 'in_transit').length,
    delivered: guides.filter((g) => g.status === 'delivered').length,
  }), [guides])

  const filtered = useMemo(() => {
    let list = guides
    if (statusFilter !== 'all') list = list.filter((g) => g.status === statusFilter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((g) =>
        g.guideNumber.toLowerCase().includes(q) ||
        g.recipient.name.toLowerCase().includes(q) ||
        g.recipient.city.toLowerCase().includes(q) ||
        g.orderNumber.toLowerCase().includes(q),
      )
    }
    return list
  }, [guides, statusFilter, search])

  function createGuide() {
    if (!newGuideData.recipient.trim()) return
    const newGuide: ShippingGuide = {
      id: `guide-${Date.now()}`,
      guideNumber: `${newGuideData.provider.toUpperCase()}-2026-05-${String(guides.length + 1).padStart(3, '0')}`,
      orderId: '',
      orderNumber: `#ORD-2026-${String(guides.length + 1).padStart(3, '0')}`,
      provider: newGuideData.provider,
      recipient: {
        name: newGuideData.recipient,
        phone: '+57',
        email: '',
        address: newGuideData.address,
        city: newGuideData.city,
        zip: '',
      },
      weight: Number(newGuideData.weight),
      value: Number(newGuideData.value),
      status: 'draft',
      trackingUrl: '',
      createdAt: new Date().toISOString(),
      events: [],
    }
    setGuides([...guides, newGuide])
    setSelectedGuide(newGuide.id)
    setShowNewGuideForm(false)
    setNewGuideData({ recipient: '', address: '', city: '', weight: '1', value: '50000', provider: 'interrapidisimo' })
  }

  function generateGuide(guideId: string) {
    setGuides(guides.map((g) =>
      g.id === guideId
        ? {
          ...g,
          status: 'generated' as const,
          trackingUrl: `https://tracking.${g.provider}.com/${g.guideNumber}`,
          events: [...g.events, {
            id: `ev-${Date.now()}`,
            type: 'manifested' as const,
            timestamp: new Date().toISOString(),
            location: 'Centro de Distribución',
            description: 'Guía generada en el sistema',
          }],
        }
        : g,
    ))
  }

  return (
    <>
      <PageHeader
        title="Logística"
        description="Generar guías, rastrear envíos e integrar operadores"
        action={
          <Button onClick={() => setShowNewGuideForm(!showNewGuideForm)}>
            <Plus className="size-4 mr-2" /> Nueva guía
          </Button>
        }
      />

      <div className="p-8 space-y-6">
        {/* FORMULARIO NUEVA GUÍA */}
        {showNewGuideForm && (
          <Card className="border-cyan-200 dark:border-cyan-800/30 bg-cyan-50 dark:bg-cyan-950/10">
            <CardHeader>
              <CardTitle className="text-sm">Generar nueva guía</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="recipient">Nombre destinatario</Label>
                  <Input
                    id="recipient"
                    value={newGuideData.recipient}
                    onChange={(e) => setNewGuideData({ ...newGuideData, recipient: e.target.value })}
                    placeholder="Juan López"
                  />
                </div>
                <div>
                  <Label htmlFor="provider">Operador</Label>
                  <select value={newGuideData.provider} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNewGuideData({ ...newGuideData, provider: e.target.value as ShippingProvider })} className="w-full px-3 py-2 rounded-lg border border-border bg-[hsl(var(--surface))] text-foreground text-sm">
                    {PROVIDERS.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor="address">Dirección</Label>
                <Textarea
                  id="address"
                  value={newGuideData.address}
                  onChange={(e) => setNewGuideData({ ...newGuideData, address: e.target.value })}
                  placeholder="Cra 7 #45-89, Apt 302"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">Ciudad</Label>
                  <Input
                    id="city"
                    value={newGuideData.city}
                    onChange={(e) => setNewGuideData({ ...newGuideData, city: e.target.value })}
                    placeholder="Bogotá"
                  />
                </div>
                <div>
                  <Label htmlFor="weight">Peso (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.1"
                    value={newGuideData.weight}
                    onChange={(e) => setNewGuideData({ ...newGuideData, weight: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="value">Valor ($)</Label>
                  <Input
                    id="value"
                    type="number"
                    value={newGuideData.value}
                    onChange={(e) => setNewGuideData({ ...newGuideData, value: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={createGuide}>Crear guía</Button>
                <Button variant="outline" onClick={() => setShowNewGuideForm(false)}>Cancelar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total guías" value={stats.total} icon={Truck} color="text-blue-700 dark:text-blue-400" />
          <StatCard label="Generadas" value={stats.generated} icon={Package} color="text-yellow-700 dark:text-yellow-400" />
          <StatCard label="En tránsito" value={stats.inTransit} icon={MapPin} color="text-[hsl(var(--primary))]" />
          <StatCard label="Entregadas" value={stats.delivered} icon={CheckCircle2} color="text-emerald-700 dark:text-emerald-400" />
        </div>

        {/* TABS Y CONTENIDO */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LISTA DE GUÍAS */}
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar guía..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* FILTROS */}
            <div className="flex flex-wrap gap-2">
              {(['all', 'generated', 'in_transit', 'delivered'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={cn(
                    'px-3 py-1.5 text-xs rounded-md transition border',
                    statusFilter === s
                      ? 'bg-[hsl(var(--muted))] text-foreground border-border'
                      : 'bg-[hsl(var(--surface))] text-muted-foreground border-transparent hover:text-foreground hover:border-border',
                  )}
                >
                  {s === 'all' ? 'Todas' : s === 'generated' ? 'Generadas' : s === 'in_transit' ? 'Tránsito' : 'Entregadas'}
                </button>
              ))}
            </div>

            {/* LISTA */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filtered.map((guide) => (
                <button
                  key={guide.id}
                  onClick={() => setSelectedGuide(guide.id)}
                  className={cn(
                    'w-full rounded-lg border p-3 text-left transition',
                    selectedGuide === guide.id
                      ? 'border-cyan-200 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-950/30'
                      : 'border-border bg-background/40 hover:border-border',
                  )}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-mono text-cyan-700 dark:text-cyan-400">{guide.guideNumber}</p>
                    <StatusBadge status={guide.status} />
                  </div>
                  <p className="text-xs text-foreground">{guide.recipient.name}</p>
                  <p className="text-xs text-muted-foreground">{guide.recipient.city}</p>
                </button>
              ))}
            </div>
          </div>

          {/* DETALLE GUÍA */}
          {selected && (
            <div className="lg:col-span-2 space-y-4">
              {/* HEADER */}
              <Card>
                <CardHeader className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">{selected.guideNumber}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{selected.orderNumber}</p>
                    </div>
                    <Badge variant="default">{PROVIDERS.find((p) => p.id === selected.provider)?.name}</Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-foreground">
                      <Package className="size-4 text-muted-foreground" />
                      <span>{selected.weight} kg • ${selected.value.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-foreground">
                      <MapPin className="size-4 text-muted-foreground" />
                      <span>{selected.recipient.address}, {selected.recipient.city}</span>
                    </div>
                    <div className="flex items-center gap-2 text-foreground">
                      <Calendar className="size-4 text-muted-foreground" />
                      <span>{new Date(selected.createdAt).toLocaleDateString('es-CO')}</span>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* ACCIONES */}
              {selected.status === 'draft' && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => generateGuide(selected.id)}
                    className="flex-1"
                  >
                    <Zap className="size-4 mr-2" />
                    Generar guía en {selected.provider}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigator.clipboard.writeText(selected.guideNumber)}
                  >
                    <Copy className="size-4" />
                  </Button>
                </div>
              )}

              {selected.trackingUrl && (
                <Button variant="outline" className="w-full">
                  <ExternalLink className="size-4 mr-2" />
                  <a href={selected.trackingUrl} target="_blank" rel="noopener noreferrer">
                    Ver en {selected.provider}
                  </a>
                </Button>
              )}

              {/* RASTREO - TIMELINE */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Rastreo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selected.events.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sin eventos registrados</p>
                  ) : (
                    selected.events.map((event, idx) => (
                      <div key={event.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={cn(
                            'size-3 rounded-full border-2',
                            idx === selected.events.length - 1 ? 'border-cyan-500 bg-cyan-500' : 'border-border bg-[hsl(var(--muted))]',
                          )} />
                          {idx < selected.events.length - 1 && <div className="w-0.5 h-8 bg-[hsl(var(--muted))] mt-1" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-medium text-foreground">{event.description}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{event.location}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(event.timestamp).toLocaleString('es-CO')}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* INFORMACIÓN DESTINATARIO */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Destinatario</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Nombre</p>
                    <p className="text-foreground">{selected.recipient.name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Teléfono</p>
                    <p className="text-foreground">{selected.recipient.phone}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="text-foreground">{selected.recipient.email || 'No disponible'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Dirección</p>
                    <p className="text-foreground">{selected.recipient.address}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* CONFIGURACIÓN DE OPERADORES */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Settings2 className="size-4" />
              Operadores disponibles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {PROVIDERS.map((provider) => (
                <div
                  key={provider.id}
                  className={cn(
                    'rounded-lg border p-4 transition',
                    provider.isConfigured
                      ? 'border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-950/20'
                      : 'border-border bg-background/40 hover:border-border',
                  )}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <span className="text-2xl">{provider.logo}</span>
                    {provider.isConfigured && <CheckCircle2 className="size-4 text-emerald-700 dark:text-emerald-400" />}
                  </div>
                  <p className="text-sm font-medium text-foreground mb-1">{provider.name}</p>
                  <p className="text-xs text-muted-foreground mb-3">{provider.apiUrl}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                  >
                    {provider.isConfigured ? 'Configurado' : 'Configurar'}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: number
  icon: React.ComponentType<{ className?: string }>
  color: string
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className="text-3xl font-bold mt-2 tabular-nums">{value}</p>
          </div>
          <Icon className={cn('size-7', color)} />
        </div>
      </CardContent>
    </Card>
  )
}

function StatusBadge({ status }: { status: GuideStatus }) {
  const map: Record<GuideStatus, { label: string; icon: React.ComponentType<{ className?: string }>; color: string }> = {
    draft: { label: 'Borrador', icon: Clock, color: 'text-muted-foreground' },
    generated: { label: 'Generada', icon: Package, color: 'text-yellow-700 dark:text-yellow-400' },
    in_transit: { label: 'Tránsito', icon: Truck, color: 'text-[hsl(var(--primary))]' },
    delivered: { label: 'Entregada', icon: CheckCircle2, color: 'text-emerald-700 dark:text-emerald-400' },
    cancelled: { label: 'Cancelada', icon: AlertCircle, color: 'text-red-700 dark:text-red-400' },
  }
  const info = map[status]
  const Icon = info.icon
  return (
    <div className="flex items-center gap-1 text-xs">
      <Icon className={cn('size-3', info.color)} />
      <span className="text-foreground">{info.label}</span>
    </div>
  )
}
